import { useCallback, useEffect, useMemo } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';

import Page from 'components/layouts/page/Page';
import AccessGuard from 'components/shared/AccessGuard';
import FilterChips from 'components/shared/FilterChips';
import DesktopOnly from 'components/shared/responsiveness/DesktopOnly';
import ControlledTable from 'components/shared/table/ControlledTable';
import useIsMobile from 'hooks/useIsMobile';
import usePagination from 'hooks/usePagination';
import { PERMISSIONS } from 'models/Profile';
import Admission from 'models/reviews/Admission';
import { admissionQueryKeys, admissionQueryParams, indexAdmissions } from 'services/api/admissions';
import { serializeSort, useAdmissionsActions, useAdmissionsStore } from 'stores/admissionsStore';

import AdmissionJobSubscriber from './AdmissionJobSubscriber';
import AdmissionsFilterBar from './AdmissionsFilterBar';
import admissionsTableColumns from './admissionsTableColumns';
import AdmissionsUploadSection from './AdmissionsUploadSection';

function AdmissionsPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const pagination = usePagination();
  const filters = useAdmissionsStore((state) => state.filters);
  const sort = useAdmissionsStore((state) => state.sort);
  const search = useAdmissionsStore((state) => state.search);
  const dateRange = useAdmissionsStore((state) => state.dateRange);
  const { removeFilter, setSort, setSearch, setFilters } = useAdmissionsActions();

  useEffect(() => () => setSearch(''), [setSearch]);

  const queryParams = useMemo(
    () => ({
      page: pagination.paging.page,
      pageSize: pagination.paging.pageSize,
      sortBy: serializeSort(sort),
      search,
      filters,
      dateRange,
      ...admissionQueryParams.index,
    }),
    [search, filters, dateRange, pagination.paging, sort]
  );

  useEffect(() => {
    pagination.setPageIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filters, dateRange]);

  const { data: reviews, isLoading } = useQuery({
    queryKey: admissionQueryKeys.index({ ...queryParams }),
    queryFn: ({ signal }) => indexAdmissions(queryParams, signal),
  });

  const columns = useMemo(() => admissionsTableColumns(), []);

  const handleRemoveFilter = useCallback(
    (filterKey: string, id: string) => {
      removeFilter(filterKey, id);
    },
    [removeFilter]
  );

  const handleApplyFilters = useCallback(
    ({ search, ...filters }) => {
      setFilters(filters);
      setSearch(search);

      if (search) {
        setSort([{ id: 'search', desc: true }]);
      } else if (sort.some(({ id }) => id === 'search')) {
        setSort([{ id: 'updatedAt', desc: true }]);
      }
    },
    [setFilters, setSort, setSearch, sort]
  );

  return (
    <Page>
      <AdmissionsFilterBar onApplyFilters={handleApplyFilters} />

      <AccessGuard accessor={PERMISSIONS.reviewAdmissionCreate}>
        <AdmissionsUploadSection />
      </AccessGuard>

      <AccessGuard accessor={PERMISSIONS.reviewAdmissionView}>
        <StyledPageSubHeader>
          <DesktopOnly>
            <FilterChips filters={filters} onRemoveFilter={handleRemoveFilter} useStore={useAdmissionsStore} />
          </DesktopOnly>
        </StyledPageSubHeader>
        <AdmissionJobSubscriber admissions={(reviews?.data ?? []) as Admission[]} />
        <Page.Content>
          <ControlledTable<Admission>
            data={(reviews?.data ?? []) as Admission[]}
            filters={filters}
            columns={columns}
            loading={isLoading}
            pageCount={reviews?.meta.totalPages ?? 1}
            pagination={pagination}
            onRowClick={(row) => navigate(`/admissions/${row.original.id}/criteria`)}
            sorting={{ sortBy: sort, setSortBy: setSort }}
            state={{
              columnVisibility: {
                referralSource: !isMobile,
                group: !isMobile,
                updatedAt: !isMobile,
              },
            }}
          />
        </Page.Content>
      </AccessGuard>
      <Outlet />
    </Page>
  );
}

export default AdmissionsPage;

const StyledPageSubHeader = styled(Page.SubHeader)`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media ${({ theme }) => theme.devices.desktop} {
    gap: 18px;
  }
`;
